import { Component, lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { authFetch } from "../auth-context";
import Lightbox from "./Lightbox";

const ImageReveal = lazy(() => import("./ImageReveal"));

/** The reveal is decoration: any failure (no WebGL, a shader error) falls back to the plain image. */
class RevealBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

let webgl: boolean | undefined;
function canReveal(): boolean {
  if (typeof window === "undefined" || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return false;
  if (webgl === undefined) {
    try { const c = document.createElement("canvas"); webgl = !!(c.getContext("webgl2") || c.getContext("webgl")); }
    catch { webgl = false; }
  }
  return webgl;
}

/**
 * An image behind the dashboard's auth: fetched with the session token only once it
 * scrolls near view, shown as a blob URL, tap to enlarge. `path` must already be a
 * validated same-origin API path (callers own that check); 410 reads as "cleared
 * after 7 days". Shared by the chat timeline and the Helpers sheet. `revealClass` turns on
 * the libraries.dev image effect: a generating mosaic while it loads, then the picture
 * dissolves in (that class sizes the mosaic).
 */
export default function ProtectedImage({path,alt,frameClass,buttonClass,placeholder="Browser capture",revealClass}:{
  path:string|null|undefined;alt:string;frameClass:string;buttonClass:string;placeholder?:string;revealClass?:string;
}) {
  const frame = useRef<HTMLDivElement>(null);
  const [visible,setVisible] = useState(() => typeof IntersectionObserver === "undefined");
  const [state,setState] = useState<{path?:string;url?:string;type?:string;error?:boolean;expired?:boolean}>({});
  const [expanded,setExpanded] = useState<string|null>(null);
  // Inside a modal <dialog> (the Helpers sheet) the preview mounts in it: the rest of the
  // page sits under the top layer and is inert.
  const [host,setHost] = useState<Element|null>(null);
  const closePreview = useCallback(() => setExpanded(null),[]);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {setVisible(true);observer.disconnect();}
    },{rootMargin:"400px"});
    if (frame.current) observer.observe(frame.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!visible || !path) return;
    const controller = new AbortController();
    let objectUrl:string|undefined;
    void (async() => {
      try {
        const response = await authFetch(path,{signal:controller.signal});
        if (response.status === 410 && !controller.signal.aborted) {
          setState({path,expired:true}); return;
        }
        const mime = (response.headers.get("Content-Type") ?? "").split(";")[0];
        if (!response.ok || !["image/png","image/jpeg","image/webp"].includes(mime!) ||
            Number(response.headers.get("Content-Length")) > 16*1024*1024) throw new Error("Unavailable");
        const blob = await response.blob();
        if (blob.size > 16*1024*1024) throw new Error("Too large");
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob); setState({path,url:objectUrl,type:mime});
      } catch {
        if (!controller.signal.aborted) setState({path,error:true});
      }
    })();
    return () => {controller.abort();if(objectUrl) URL.revokeObjectURL(objectUrl);};
  }, [visible,path]);
  const current = state.path === path ? state : {};
  const [revealed,setRevealed] = useState(false);
  const revealDone = useCallback(() => setRevealed(true),[]);
  const revealing = !!revealClass && !revealed && !!path && !current.error && !current.expired && canReveal();
  const still = <span>{placeholder}</span>;
  return <><div ref={frame} className={frameClass}>
    {revealing ? <RevealBoundary fallback={current.url ? <img src={current.url} alt={alt} /> : still}>
      <Suspense fallback={still}><ImageReveal src={current.url} className={revealClass} onDone={revealDone} /></Suspense>
    </RevealBoundary> : current.url ? <button type="button" className={buttonClass}
      aria-label={"Enlarge " + alt} title="View full size"
      onClick={() => {setHost(frame.current?.closest("dialog") ?? null);setExpanded(current.url!);}}>
      <img src={current.url} alt={alt} loading="lazy" decoding="async" /></button> :
      <span>{current.expired ? "Images cleared after 7 days" : current.error || !path ? "Image unavailable" : placeholder}</span>}
  </div>{expanded && expanded === current.url && <Lightbox src={expanded} filename={alt}
    mimeType={current.type} onClose={closePreview} manageFocus container={host} />}</>;
}
